import { useEffect, useRef } from 'react';

/**
 * 人物写真をサーバーへ送ってポーズ推定する（「画像から作成」「複数画像からアニメーションを作成」の
 * どちらも）操作の直前に挟む、肖像権配慮の確認モーダル。
 * 実装は ExternalLinkModal.tsx / PublishConfirmModal.tsx の作法を踏襲
 * （Escape で閉じる・body スクロールロック・初期フォーカス等）。
 */
interface PhotoConsentModalProps {
  open: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}

export default function PhotoConsentModal({ open, onCancel, onConfirm }: PhotoConsentModalProps) {
  const confirmBtnRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) return;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCancel();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open, onCancel]);

  useEffect(() => {
    if (!open) return;
    confirmBtnRef.current?.focus();
  }, [open]);

  if (!open) return null;

  return (
    <div className="modal" role="dialog" aria-modal="true" aria-labelledby="photo-consent-modal-title">
      <div className="modal__backdrop" onClick={onCancel} />
      <div className="modal__panel" onClick={(e) => e.stopPropagation()}>
        <div className="modal__header">
          <h3 id="photo-consent-modal-title" className="modal__title">肖像権への配慮をお願いします</h3>
          <button type="button" className="modal__close" onClick={onCancel} aria-label="閉じる">×</button>
        </div>
        <ul className="modal__list">
          <li>必ず被写体となる人に承諾を得た写真を用いてください。</li>
          <li>選択した写真はポーズ推定のためサーバーへ送信されます。</li>
          <li>第三者や他人が写った写真を、本人の同意なく使用しないでください。</li>
        </ul>
        <div className="modal__actions">
          <button type="button" className="btn" onClick={onCancel}>キャンセル</button>
          <button type="button" className="btn btn--primary" onClick={onConfirm} ref={confirmBtnRef}>
            同意して続ける
          </button>
        </div>
      </div>
    </div>
  );
}
